import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  BarChart3, Trophy, TrendingUp, Package, 
  AlertTriangle, Target, Award, Star, CheckCircle, Clock, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// Performance thresholds (seuils officiels demandés)
const PERFORMANCE_THRESHOLDS = {
  excellent: { min_colis: 575, label: 'Très bon', color: 'green' },
  good: { min_colis: 480, label: 'Bon', color: 'blue' },
  normal: { min_colis: 430, label: 'Normal', color: 'orange' },
  insufficient: { min_colis: 0, label: 'Insuffisant', color: 'red' }
};

// Alert thresholds for reclamations
const ALERT_THRESHOLDS = {
  ok: { max_reclamations: 3 },
  warning: { max_reclamations: 6 },
  critical: { max_reclamations: Infinity }
};

// Helper functions
const getPerformanceLevel = (colisLivres) => {
  if (colisLivres >= PERFORMANCE_THRESHOLDS.excellent.min_colis) return 'excellent';
  if (colisLivres >= PERFORMANCE_THRESHOLDS.good.min_colis) return 'good';
  if (colisLivres >= PERFORMANCE_THRESHOLDS.normal.min_colis) return 'normal';
  return 'insufficient';
};

const getAlertLevel = (reclamations) => {
  if (reclamations <= ALERT_THRESHOLDS.ok.max_reclamations) return 'ok';
  if (reclamations <= ALERT_THRESHOLDS.warning.max_reclamations) return 'warning';
  return 'critical';
};

const getPerformanceColor = (level) => {
  const colors = {
    excellent: 'text-green-600 bg-green-100 border-green-200',
    good: 'text-blue-600 bg-blue-100 border-blue-200',
    normal: 'text-orange-600 bg-orange-100 border-orange-200',
    insufficient: 'text-red-600 bg-red-100 border-red-200'
  };
  return colors[level] || 'text-gray-600 bg-gray-100 border-gray-200';
};

const getPerformanceLabel = (level) => {
  const labels = {
    excellent: 'Très bon',
    good: 'Bon',
    normal: 'Normal',
    insufficient: 'Insuffisant'
  };
  return labels[level] || 'N/A';
};

// Date range helpers
const getWeekRange = (offset = 0) => {
  const now = new Date();
  const currentDay = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - currentDay + 1 + (offset * 7));
  monday.setHours(0, 0, 0, 0);
  
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0],
    label: `Semaine du ${monday.toLocaleDateString('fr-FR')} au ${sunday.toLocaleDateString('fr-FR')}`
  };
};

const getMonthRange = (offset = 0) => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  
  return {
    start: firstDay.toISOString().split('T')[0],
    end: lastDay.toISOString().split('T')[0],
    label: `${monthNames[firstDay.getMonth()]} ${firstDay.getFullYear()}`
  };
};

// Progress Bar Component
const ProgressBar = ({ value, max, color = 'bg-blue-500' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  
  return (
    <div className="w-full">
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Main Component
const EmployeeStatistics = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [allDriversStats, setAllDriversStats] = useState([]);

  const isApprovedWorkedSchedule = (schedule) => {
    const status = String(schedule?.status || '').trim().toLowerCase();
    const validation = String(schedule?.validation_status || '').trim().toLowerCase();
    const isApproved = validation === 'approved' || validation === 'validated';
    return status === 'present' && isApproved;
  };
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [offset, setOffset] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('');

  // Get date range based on view mode
  const getDateRange = useCallback(() => {
    if (viewMode === 'week') {
      return getWeekRange(offset);
    }
    return getMonthRange(offset);
  }, [viewMode, offset]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end, label } = getDateRange();
      setPeriodLabel(label);

      // Fetch my schedules
      const [scheduleRes, reclamRes] = await Promise.all([
        axios.get(`${API}/schedule/user/${user.user_id}?start_date=${start}&end_date=${end}`, {
          withCredentials: true
        }),
        axios.get(`${API}/reclamations/my?date_from=${start}&date_to=${end}`, {
          withCredentials: true
        }).catch(() => ({ data: { reclamations: [] } }))
      ]);

      const mySchedules = (scheduleRes.data || []).filter((s) =>
        s.delivered !== null &&
        s.delivered !== undefined &&
        isApprovedWorkedSchedule(s)
      );
      
      setSchedules(mySchedules);
      setReclamations(reclamRes.data?.reclamations || []);

      // Classement global : réservé aux admins (un salarié ne voit que ses propres données)
      if (user?.role === 'admin') try {
        const globalRes = await axios.get(`${API}/schedules?start_date=${start}&end_date=${end}`, {
          withCredentials: true
        });
        
        // Group schedules by user to calculate ranking
        const userMap = {};
        (globalRes.data || []).forEach(s => {
          if (!isApprovedWorkedSchedule(s)) return;
          if (s.delivered === null || s.delivered === undefined) return;
          
          // Filter for Colis Privé sites only
          const siteName = (s.site_name || '').toLowerCase();
          const isColisPrive = siteName.includes('colis') || siteName.includes('privé') || siteName.includes('prive');
          if (!isColisPrive) return;
          
          if (!userMap[s.user_id]) {
            userMap[s.user_id] = {
              user_id: s.user_id,
              name: s.user_name || 'Inconnu',
              colis_livres: 0,
              colis_retours: 0
            };
          }
          userMap[s.user_id].colis_livres += (s.delivered || 0);
          userMap[s.user_id].colis_retours += (s.returned || 0);
        });
        
        // Calculate score and sort
        const drivers = Object.values(userMap).map(d => {
          const tauxLivraison = d.colis_livres > 0 
            ? ((d.colis_livres - d.colis_retours) / d.colis_livres) * 100 
            : 0;
          const score = d.colis_livres * 0.7 + tauxLivraison * 0.3;
          return { ...d, score };
        }).sort((a, b) => b.score - a.score);
        
        setAllDriversStats(drivers);
      } catch {
        // If we can't access global stats, that's fine
        setAllDriversStats([]);
      }

    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [getDateRange, user.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate my statistics
  const myStats = useMemo(() => {
    const colisLivres = schedules.reduce((sum, s) => sum + (s.delivered || 0), 0);
    const colisRetours = schedules.reduce((sum, s) => sum + (s.returned || 0), 0);
    const nonCollected = schedules.reduce((sum, s) => sum + (s.non_collected || 0), 0);
    const picked = schedules.reduce((sum, s) => sum + (s.picked || 0), 0);
    const missions = schedules.length;
    const myReclamations = reclamations.length;

    const tauxLivraison = colisLivres > 0 
      ? Math.round(((colisLivres - colisRetours) / colisLivres) * 10000) / 100
      : 0;

    const performanceLevel = getPerformanceLevel(colisLivres);
    const alertLevel = getAlertLevel(myReclamations);

    return {
      colisLivres,
      colisRetours,
      nonCollected,
      picked,
      missions,
      reclamations: myReclamations,
      tauxLivraison,
      performanceLevel,
      alertLevel
    };
  }, [schedules, reclamations]);

  // Calculate my rank
  const myRank = useMemo(() => {
    if (!allDriversStats.length) return null;
    const rank = allDriversStats.findIndex(d => d.user_id === user.user_id);
    if (rank === -1) return null;
    return {
      position: rank + 1,
      total: allDriversStats.length
    };
  }, [allDriversStats, user.user_id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-red-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-2" />
          <p>{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Period Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Mes Statistiques
          </h2>
          <p className="text-sm text-gray-500">Performance et suivi de vos livraisons</p>
        </div>
        
        {/* Period Navigation */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <Button
              size="sm"
              variant={viewMode === 'week' ? 'default' : 'ghost'}
              onClick={() => { setViewMode('week'); setOffset(0); }}
              className={viewMode === 'week' ? 'bg-blue-600' : ''}
            >
              Semaine
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'month' ? 'default' : 'ghost'}
              onClick={() => { setViewMode('month'); setOffset(0); }}
              className={viewMode === 'month' ? 'bg-blue-600' : ''}
            >
              Mois
            </Button>
          </div>
          
          <div className="flex items-center gap-1">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setOffset(prev => prev - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[180px] text-center">
              {periodLabel}
            </span>
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setOffset(prev => prev + 1)}
              disabled={offset >= 0}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Colis livrés</p>
                <p className="text-2xl font-bold text-gray-900">{myStats.colisLivres}</p>
              </div>
              <Package className="w-8 h-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Taux livraison</p>
                <p className="text-2xl font-bold text-gray-900">{myStats.tauxLivraison}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Retours</p>
                <p className="text-2xl font-bold text-gray-900">{myStats.colisRetours}</p>
              </div>
              <Target className="w-8 h-8 text-orange-500 opacity-80" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Réclamations</p>
                <p className="text-2xl font-bold text-gray-900">{myStats.reclamations}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Card */}
      <Card className={`border-2 ${getPerformanceColor(myStats.performanceLevel)}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Award className="w-5 h-5" />
              Votre niveau de performance
            </span>
            <span className={`px-4 py-2 rounded-full text-sm font-bold ${getPerformanceColor(myStats.performanceLevel)}`}>
              {getPerformanceLabel(myStats.performanceLevel)}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress towards next level */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Progression vers le niveau supérieur</span>
                <span className="font-medium">{myStats.colisLivres} colis</span>
              </div>
              <ProgressBar 
                value={myStats.colisLivres}
                max={myStats.performanceLevel === 'excellent' ? myStats.colisLivres : 
                     myStats.performanceLevel === 'good' ? PERFORMANCE_THRESHOLDS.excellent.min_colis :
                     myStats.performanceLevel === 'normal' ? PERFORMANCE_THRESHOLDS.good.min_colis :
                     PERFORMANCE_THRESHOLDS.normal.min_colis}
                color={
                  myStats.performanceLevel === 'excellent' ? 'bg-green-500' :
                  myStats.performanceLevel === 'good' ? 'bg-blue-500' :
                  myStats.performanceLevel === 'normal' ? 'bg-orange-500' :
                  'bg-red-500'
                }
              />
            </div>
            
            {/* Performance thresholds info */}
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(PERFORMANCE_THRESHOLDS).map(([key, threshold]) => (
                <span 
                  key={key}
                  className={`px-2 py-1 rounded border ${
                    myStats.performanceLevel === key ? 'border-2 font-bold' : 'opacity-60'
                  } ${getPerformanceColor(key)}`}
                >
                  {threshold.label}: ≥{threshold.min_colis}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ranking Card */}
      {myRank && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                  myRank.position <= 3 ? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  {myRank.position === 1 && <span className="text-3xl">🥇</span>}
                  {myRank.position === 2 && <span className="text-3xl">🥈</span>}
                  {myRank.position === 3 && <span className="text-3xl">🥉</span>}
                  {myRank.position > 3 && (
                    <span className="text-2xl font-bold text-blue-600">#{myRank.position}</span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Votre position dans le classement</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {myRank.position}<sup>e</sup> sur {myRank.total} livreurs
                  </p>
                </div>
              </div>
              <Trophy className="w-12 h-12 text-yellow-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missions Detail */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Détail des missions ({myStats.missions})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {schedules.length === 0 ? (
            <p className="text-center text-gray-500 py-4">
              Aucune mission pour cette période
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-left p-3 font-medium text-gray-600">Site</th>
                    <th className="text-right p-3 font-medium text-gray-600">Livrés</th>
                    <th className="text-right p-3 font-medium text-gray-600">Retours</th>
                    <th className="text-right p-3 font-medium text-gray-600">Non Coll.</th>
                    <th className="text-center p-3 font-medium text-gray-600">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {schedules.map((schedule) => (
                    <tr key={schedule.schedule_id} className="hover:bg-gray-50">
                      <td className="p-3">{new Date(schedule.date).toLocaleDateString('fr-FR')}</td>
                      <td className="p-3">{schedule.site_name || '-'}</td>
                      <td className="p-3 text-right font-medium">{schedule.delivered || 0}</td>
                      <td className="p-3 text-right text-orange-600">{schedule.returned || 0}</td>
                      <td className="p-3 text-right text-red-600">{schedule.non_collected || 0}</td>
                      <td className="p-3 text-center">
                        {(() => {
                          const validation = String(schedule.validation_status || '').trim().toLowerCase();
                          const isApproved = validation === 'approved' || validation === 'validated';
                          const isRejected = validation === 'rejected';
                          const badgeClass = isApproved
                            ? 'bg-green-100 text-green-700'
                            : isRejected
                              ? 'bg-red-100 text-red-700'
                              : 'bg-orange-100 text-orange-700';
                          const badgeLabel = isApproved ? 'Validé' : isRejected ? 'Rejeté' : 'En attente de validation';
                          return (
                            <span className={`px-2 py-1 rounded-full text-xs ${badgeClass}`}>
                              {badgeLabel}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-medium text-gray-700 mb-2">Seuils de performance (par semaine)</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Très bon: ≥575 colis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span>Bon: ≥480 colis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span>Normal: ≥430 colis</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>Insuffisant: &lt;430 colis</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeStatistics;
